/**
 * Engine grid path kit — Phaser-free ortho BFS next step.
 * Domain passable / occupancy stays in feature rules (closure).
 */

export type GridCell = { col: number; row: number };

const ORTHO: ReadonlyArray<readonly [number, number]> = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
];

export function cellKey(col: number, row: number): string {
  return `${col},${row}`;
}

export function chebyshev(
  c0: number,
  r0: number,
  c1: number,
  r1: number,
): number {
  return Math.max(Math.abs(c1 - c0), Math.abs(r1 - r0));
}

export type BfsNextStepOpts = {
  /** When BFS fails, greedily step toward goal among passable ortho neighbors. */
  greedyFallback?: boolean;
};

/**
 * First ortho step along a shortest BFS path toward goal.
 * `passable` owns bounds / blockers / merge exceptions.
 */
export function bfsNextStep(
  fromCol: number,
  fromRow: number,
  goalCol: number,
  goalRow: number,
  passable: (col: number, row: number) => boolean,
  opts: BfsNextStepOpts = {},
): GridCell | null {
  if (fromCol === goalCol && fromRow === goalRow) return null;
  const start = cellKey(fromCol, fromRow);
  const goal = cellKey(goalCol, goalRow);
  const queue: GridCell[] = [{ col: fromCol, row: fromRow }];
  const came = new Map<string, string | null>();
  came.set(start, null);
  let found = false;
  while (queue.length > 0) {
    const cur = queue.shift()!;
    if (cellKey(cur.col, cur.row) === goal) {
      found = true;
      break;
    }
    for (const [dc, dr] of ORTHO) {
      const nc = cur.col + dc;
      const nr = cur.row + dr;
      const k = cellKey(nc, nr);
      if (came.has(k)) continue;
      if (!passable(nc, nr)) continue;
      came.set(k, cellKey(cur.col, cur.row));
      queue.push({ col: nc, row: nr });
    }
  }
  if (!found) {
    if (opts.greedyFallback === false) return null;
    let best: GridCell | null = null;
    let bestD = chebyshev(fromCol, fromRow, goalCol, goalRow);
    for (const [dc, dr] of ORTHO) {
      const nc = fromCol + dc;
      const nr = fromRow + dr;
      if (!passable(nc, nr)) continue;
      const d = chebyshev(nc, nr, goalCol, goalRow);
      if (d < bestD) {
        bestD = d;
        best = { col: nc, row: nr };
      }
    }
    return best;
  }
  let cur = goal;
  const path: string[] = [cur];
  while (came.get(cur)) {
    const prev = came.get(cur)!;
    path.push(prev);
    cur = prev;
  }
  path.reverse();
  if (path.length < 2) return null;
  const step = path[1]!.split(",").map(Number);
  return { col: step[0]!, row: step[1]! };
}
